import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosInstance } from "axios";
import { IDelegateRoleGroup } from "../interfaces/IDelegateRoleGroup";
import { DelegateRoleGroupService } from "../services/delegateRoleGroupService";
import { RootState } from "./store";

// Selectors
export const selectDelegateRoleGroupsLoading = (state: RootState) => state.delegateRoleGroups.loading;
export const selectDelegateRoleGroups = (state: RootState) => state.delegateRoleGroups.delegateRoleGroups;

// Async actions
export const fetchDelegateRoleGroups = createAsyncThunk<IDelegateRoleGroup[], AxiosInstance, { state: RootState }>(
    "delegateRoleGroups/fetch",
    async (apiClient, { getState }) => {
        const { delegateRoleGroups, lastFetched } = getState().delegateRoleGroups;
        if (delegateRoleGroups.length > 0 && Date.now() - lastFetched < 5 * 60 * 1000) {
            return delegateRoleGroups;
        }
        const delegateRoleGroupService = new DelegateRoleGroupService(apiClient);
        return await delegateRoleGroupService.getAll<IDelegateRoleGroup[]>();
    },
    {
        condition: (_apiClient, { getState }) => {
            const { delegateRoleGroups, lastFetched } = getState().delegateRoleGroups;
            return !(delegateRoleGroups.length > 0 && Date.now() - lastFetched < 5 * 60 * 1000);
        }
    }
);

export const addDelegateRoleGroup = createAsyncThunk<IDelegateRoleGroup, { delegateRoleGroup: IDelegateRoleGroup; apiClient: AxiosInstance }>(
    "delegateRoleGroups/add",
    async ({ delegateRoleGroup, apiClient }) => {
        const delegateRoleGroupService = new DelegateRoleGroupService(apiClient);
        return await delegateRoleGroupService.create(delegateRoleGroup);
    }
);

export const updateDelegateRoleGroup = createAsyncThunk<IDelegateRoleGroup, { delegateRoleGroupId: string; updatedDelegateRoleGroup: IDelegateRoleGroup; apiClient: AxiosInstance }>(
    "delegateRoleGroups/update",
    async ({ delegateRoleGroupId, updatedDelegateRoleGroup, apiClient }) => {
        const delegateRoleGroupService = new DelegateRoleGroupService(apiClient);
        return await delegateRoleGroupService.update(delegateRoleGroupId, updatedDelegateRoleGroup);
    }
);

export const deleteDelegateRoleGroup = createAsyncThunk<string, { delegateRoleGroupId: string; apiClient: AxiosInstance }>(
    "delegateRoleGroups/delete",
    async ({ delegateRoleGroupId, apiClient }) => {
        const delegateRoleGroupService = new DelegateRoleGroupService(apiClient);
        await delegateRoleGroupService.delete(delegateRoleGroupId);
        return delegateRoleGroupId;
    }
);

const delegateRoleGroupsSlice = createSlice({
    name: "delegateRoleGroups",
    initialState: {
        delegateRoleGroups: [] as IDelegateRoleGroup[],
        loading: false,
        lastFetched: 0
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchDelegateRoleGroups.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchDelegateRoleGroups.fulfilled, (state, action) => {
                state.loading = false;
                state.delegateRoleGroups = action.payload;
                state.lastFetched = Date.now();
            })
            .addCase(addDelegateRoleGroup.fulfilled, (state, action) => {
                state.delegateRoleGroups.push(action.payload);
            })
            .addCase(updateDelegateRoleGroup.fulfilled, (state, action) => {
                state.delegateRoleGroups = state.delegateRoleGroups.map(group =>
                    group.id === action.payload.id ? action.payload : group
                );
            })
            .addCase(deleteDelegateRoleGroup.fulfilled, (state, action) => {
                state.delegateRoleGroups = state.delegateRoleGroups.filter(group => group.id !== action.payload);
            });
    }
});

export default delegateRoleGroupsSlice.reducer;
